import { Dialog, DialogFooter, Icon, Label, Text } from "@fluentui/react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { FormSpinner } from "../../components/FormSpinner";
import { Flex, HSpace, StyledPrimaryButton } from "../../components/styled";
import { Select } from "../../components/Select";
import { useSelector } from "../../store/hooks";
import {
  changeLocationRoutine,
  setChangeLocationDialog,
} from "../../store/reports-page/reportsPage";
import { selectAllLocations } from "../../store/locations/locations";
import { selectAllClients } from "../../store/clients/clients";
import { Client } from "../../types/types";

type LocationOption = {
  label: string;
  value: number;
} | null;

const findRootClientId = (
  clientId: number,
  clientEntities: Record<number, Client>,
): number => {
  let current = clientEntities[clientId];
  while (current?.parentClientId) {
    current = clientEntities[current.parentClientId];
  }
  return current?.clientId ?? clientId;
};

const getDescendantClientIds = (
  rootId: number,
  clients: Client[],
): Set<number> => {
  const result = new Set<number>([rootId]);
  let added = true;
  while (added) {
    added = false;
    for (const c of clients) {
      if (!result.has(c.clientId) && result.has(c.parentClientId)) {
        result.add(c.clientId);
        added = true;
      }
    }
  }
  return result;
};

export const ChangeLocationDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { report } = useSelector(
    (s) => s.reportsPage.changeLocationDialog,
  );
  const isLoading = useSelector(
    (s) => s.reportsPage.changeLocationLoadStatus === "loading",
  );
  const locations = useSelector(selectAllLocations);
  const clients = useSelector(selectAllClients);
  const clientEntities = useSelector((s) => s.clients.entities);

  const [location, setLocation] = useState<LocationOption>(null);

  const locationOptions = useMemo(() => {
    const currentLocation = locations.find(
      (loc) => loc.locationId === report?.locationId,
    );
    const currentClientId = currentLocation?.webBasePartnerNumber
      ? Number(currentLocation.webBasePartnerNumber)
      : null;

    let allowedClientIds: Set<number> | null = null;
    if (currentClientId != null) {
      const rootId = findRootClientId(currentClientId, clientEntities);
      allowedClientIds = getDescendantClientIds(rootId, clients);
    }

    return locations
      .filter((loc) => {
        if (loc.locationId === report?.locationId) return false;
        if (!allowedClientIds) return true;
        const locClientId = loc.webBasePartnerNumber
          ? Number(loc.webBasePartnerNumber)
          : null;
        return locClientId != null && allowedClientIds.has(locClientId);
      })
      .map((loc) => ({
        label: loc.clientName
          ? `${loc.locationName} (${loc.clientName})`
          : loc.locationName,
        value: loc.locationId,
      }));
  }, [locations, report?.locationId, clients, clientEntities]);

  const close = () => {
    dispatch(
      setChangeLocationDialog({
        report: null,
        isOpen: false,
      }),
    );
  };

  return (
    <Dialog
      minWidth={700}
      hidden={false}
      dialogContentProps={{
        showCloseButton: true,
        title: (
          <Flex>
            <Icon iconName="Switch" /> <HSpace />
            <div>{t("ram.changeLocation.label")}</div>
          </Flex>
        ),
      }}
      onDismiss={close}
    >
      <Text
        block
        styles={{
          root: {
            marginBottom: 10,
          },
        }}
      >
        {t("ram.changeLocationDialogDescription.label")}
      </Text>
      <div>
        <Label>{t("ram.locationList.locationName")}</Label>
        <Select
          options={locationOptions}
          value={location}
          onChange={(option: LocationOption) => setLocation(option)}
          isClearable
          menuPosition="fixed"
          placeholder={t("greco.form.searchPlaceholder")}
          noOptionsMessage={() => t("greco.noResults")}
        />
      </div>
      <DialogFooter>
        <StyledPrimaryButton
          text={t("ram.changeLocation.label")}
          onClick={() => {
            dispatch(
              changeLocationRoutine.trigger({
                locationId: report.locationId,
                reportId: report.reportId,
                newLocationId: location?.value,
              }),
            );
          }}
          disabled={isLoading || !location?.value}
        />
      </DialogFooter>
      {isLoading && <FormSpinner />}
    </Dialog>
  );
};
